import apiTranslations from './sk.api.ts';
import primeVueTranslations from './sk.primevue.ts';
import {
  generalTerm,
  generalTermFilter,
} from './sk.shared.ts';

import npm from '@/generated/npm/locales/sk.npm.ts';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  EAttributeLabel,
} from '@/types/attribute-type.ts';
import {
  EWeekday,
} from '@/types/calendar.ts';
import {
  EMonth,
} from '@/types/date.ts';
import {
  ETimeseriesMeaning,
} from '@/types/openapi-type.ts';
import {
  EVisualisation,
} from '@/types/performance-dashboard.ts';
import {
  TUnitSystem,
} from '@/types/unit-of-measurement.ts';
import {
  getComponentScopedTranslationMap,
} from '@/utils/i18n.ts';

const Visualisation: Record<EVisualisation, string> = {
  [EVisualisation.CLUSTERED_STACKED_BAR_CHART]: 'Zoskupený skladaný stĺpcový graf',
  [EVisualisation.PARETO_CHART]: 'Paretov diagram',
  [EVisualisation.STACKED_BAR_CHART]: 'Skladaný stĺpcový graf',
  [EVisualisation.STACKED_POLAR_CHART]: 'Skladaný polárny graf',
  [EVisualisation.TABLE]: 'Tabuľka',
};

const unitSystem: Record<TUnitSystem, string> = {
  metric: 'Metrická sústava',
  imperial: 'Imperiálna sústava',
};

export default {
  generalTermFilter,
  generalTerm,
  api: apiTranslations,
  primeVue: primeVueTranslations,
  npm,
  actions: {
    clear: 'Vymazať',
    accept: 'Prijať',
    login: 'Prihlásiť sa',
    reset: 'Obnoviť',
    search: 'Hľadať',
    select: 'Vybrať',
    unlink: 'Odpojiť',
  },
  alerts: {
    saveFailed: 'Uloženie zlyhalo',
    deleteSuccess: 'Úspešne odstránené',
    valueAboveMax: 'Hodnota by mala byť nižšia ako',
  },

  assetManagement: {
    confirmResurrection: {
      header: 'Uviesť "{item}" späť do prevádzky?',
      message: 'Vyradené aktívum sa vráti do prevádzky.',
    },
    confirmDelete: {
      header: 'Odstrániť alebo vyradiť "{item}"?',
      message: 'Aktíva s údajmi, aj v podradených aktívach, sa namiesto toho vyradia a neskôr ich možno opäť uviesť do prevádzky.',
    },
    includeRetiredAssets: 'Zobraziť vyradené',
    resurrectAsset: 'Uviesť späť do prevádzky',
  },
  buttons: {
    acknowledge: 'Potvrdiť',
    analogInputs: 'Analógové vstupy',
    cancel: 'Zrušiť',
    digitalInputs: 'Digitálne vstupy',
    editOverview: 'Upraviť prehľad',
    loadList: 'Zoznam vsádzok',
    upload: 'Nahrať',
  },
  calendar: {
    occurs: 'Opakuje sa',
    on: 'Dňa',
    startingOn: 'začínajúc od',
  },
  charts: {
    position: {
      left: 'Vľavo',
      right: 'Vpravo',
    },
    newAxis: 'Nová os',
    noAlarms: 'Žiadne alarmy',
  },
  fullscreen: {
    exitFullscreen: 'Ukončiť celú obrazovku',
    fullscreen: 'Celá obrazovka',
  },
  measurementUnit: {
    noGroup: 'Iné',
  },
  attributeUnit: {
    [EAttributeLabel.WEIGHT]: 'Hmotnosť',
    [EAttributeLabel.LENGTH]: 'Dĺžka',
    [EAttributeLabel.DIAMETER]: 'Priemer',
    [EAttributeLabel.SURFACE_HARDNESS]: 'Povrchová tvrdosť',
    [EAttributeLabel.SURFACE_HARDNESS_MIN]: 'Minimálna povrchová tvrdosť',
    [EAttributeLabel.SURFACE_HARDNESS_MAX]: 'Maximálna povrchová tvrdosť',
    [EAttributeLabel.CORE_HARDNESS]: 'Tvrdosť jadra',
    [EAttributeLabel.CORE_HARDNESS_MIN]: 'Minimálna tvrdosť jadra',
    [EAttributeLabel.CORE_HARDNESS_MAX]: 'Maximálna tvrdosť jadra',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_MIN]: 'ECD min',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_MAX]: 'ECD max',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_HARDNESS]: 'Tvrdosť ECD',
    neuAttributePlaceholder: 'Menovka atribútu',
  },
  equipmentGasName: {
    [ETimeseriesMeaning.ACETYLENE_C2H2_FLOW_SETPOINT]: 'Acetylén',
    [ETimeseriesMeaning.AIR_FLOW_SETPOINT]: 'Vzduch',
    [ETimeseriesMeaning.AMMONIA_NH3_FLOW_SETPOINT]: 'Amóniak',
    [ETimeseriesMeaning.ARGON_FLOW_SETPOINT]: 'Argón',
    [ETimeseriesMeaning.CARBON_DIOXIDE_CO2_FLOW_SETPOINT]: 'CO2',
    [ETimeseriesMeaning.CARBON_MONOXIDE_CO_FLOW_SETPOINT]: 'CO',
    [ETimeseriesMeaning.DISSOCIATED_AMMONIA_NH3_FLOW_SETPOINT]: 'Disociovaný amóniak',
    [ETimeseriesMeaning.ENDO_GAS_NATURAL_FLOW_SETPOINT]: 'Endo Gas Natural',
    [ETimeseriesMeaning.ENDO_GAS_PROPANE_FLOW_SETPOINT]: 'Endo Gas Propán',
    [ETimeseriesMeaning.ETHENE_C2H4_FLOW_SETPOINT]: 'Etén',
    [ETimeseriesMeaning.HELIUM_FLOW_SETPOINT]: 'Hélium',
    [ETimeseriesMeaning.HYDROGEN_H2_FLOW_SETPOINT]: 'Vodík',
    [ETimeseriesMeaning.METHANOL_CH3OH_FLOW_SETPOINT]: 'Metanol',
    [ETimeseriesMeaning.NATURAL_GAS_FLOW_SETPOINT]: 'Zemný plyn',
    [ETimeseriesMeaning.NITROGEN_FLOW_SETPOINT]: 'Dusík',
    [ETimeseriesMeaning.PROPANE_C3H8_FLOW_SETPOINT]: 'Propán',
    [ETimeseriesMeaning.WATER_FLOW_SETPOINT]: 'Voda',
    [ETimeseriesMeaning.NITROUS_OXIDE_N2O_FLOW_SETPOINT]: 'N2O',
    [ETimeseriesMeaning.METHANE_CH4_FLOW_SETPOINT]: 'Metán',
    [ETimeseriesMeaning.OXYGEN_O2_FLOW_SETPOINT]: 'Kysík',
  },
  // single words or small phrases WITHOUT CONTEXT
  common: {
    min: 'Minimum',
    max: 'Maximum',
    emptyTable: 'Nenašli sa žiadne zodpovedajúce údaje.',
    attribute: 'Atribút',
    revealItem: 'Zobraziť {item}',
    hideItem: 'Skryť {item}',
    numberedItem: '{item} {number}',
    valueRange: 'Rozsah hodnôt',
    all: 'Všetky',
    user: 'Používateľ',
    date: 'Dátum',
    actions: 'Akcie',
    yes: 'Áno',
    no: 'Nie',
    clear: 'Vymazať',
    delete: 'Odstrániť',
    save: 'Uložiť',
    duration: 'Trvanie',
    download: 'Stiahnuť {item}',
    comment: 'Komentár',
    print: 'Tlačiť',
    chart: 'Graf',
    description: generalTerm.description,
    status: 'Stav',
    assignee: 'Zodpovedná osoba',
    temperature: 'Teplota',
    name: 'Názov',
    filters: 'Filtre',
    overview: 'Prehľad',
    type: 'Typ',
    set: 'Nastaviť',
    username: 'Používateľské meno',
    password: 'Heslo',
    include: 'Zahrnúť',
    exclude: 'Vylúčiť',
    custom: 'Vlastné',
    loading: 'Načítava sa',
    retired: 'vyradený',
    carbon: 'Uhlík',
    enterForNewRow: 'Stlačte "Enter" pre pridanie nového riadku',
    now: 'Teraz',
    note: 'Poznámka',
    target: 'Cieľ',
    source: 'Zdroj',
    skip: 'Preskočiť',
    reject: 'Zamietnuť',
    tag: 'Štítok | Štítky',
    withoutTag: 'Bez štítku',
    maintenance: 'Údržba',
    filter: 'Filter | Filtre',
    navigation: 'Navigácia',
    calendar: 'Kalendár',
    noAccessToAction: 'Nemáte prístup k tejto akcii.',
    tiny: 'Malý',
    large: 'Veľký',
    size: 'Veľkosť',
    appearance: 'Vzhľad',
    notification: 'Oznámenie | Oznámenia',
    meaning: 'Zmysel | Zmysly',
    result: 'Výsledok',
    toggle: 'Prepínač',
    statistics: 'Štatistika',
    rating: 'Hodnotenie',
    showTable: 'Zobraziť tabuľku',
    showChart: 'Zobraziť graf',
    configuration: 'Konfigurácia',
    passed: 'Prešiel',
    failed: 'Zlyhal',
    section: 'Sekcia',
    capacity: 'kapacita | Kapacity',
    gas: 'Plyn | Plyny',
    capability: 'Schopnosť | Schopnosti',
    certificate: 'Certifikát | Certifikáty',
    model: 'Model | Modely',
    createItem: 'Vytvoriť {item}',
    legend: 'Legenda',
    linkItem: 'Prepojiť {item}',
    today: 'Dnes',
    earlier: 'Skôr',
    addItem: 'Pridať {item}',
    image: 'Obrázok',
    file: 'Súbor',
    pasteFromClipboard: 'Vložiť zo schránky',
    noItemInClipboard: 'Žiadny {item} v schránke',
    assetNavigation: 'Navigácia v zariadeniach',
    noRecipeSelected: 'Nie je vybraný recept',
    addAssessment: 'Pridať hodnotenie',
    addRow: 'Pridať riadok',
    setAll: 'Nastaviť všetko',
    resetAll: 'Obnoviť všetko',
    unlock: 'Odomknúť',
    notAvailable: 'N/A',
    stop: 'Zastaviť',
    loadItem: 'Načítať {item}',
    add: 'Pridať',
    valueAtTime: '{value} o {time}',
    timeline: 'Časová os',
    Ferrite: 'Ferit',
    Austenite: 'Austenit',
    Martensite: 'Martenzit',
    labelInUnit: '{label} v {unit}',
    default: 'Predvolené',
    settings: 'Nastavenia',
    version: 'Verzia',
    logout: 'Odhlásiť sa',
    confirm: 'Potvrdiť',
    edit: 'Upraviť',
    start: 'Spustiť',
    confirmUnlink: {
      header: 'Odpojiť "{item}"?',
      message: 'Obe položky zostanú zachované a možno ich znova prepojiť.',
    },
    confirmDelete: {
      header: 'Odstrániť "{item}"?',
      message: 'Túto akciu nie je možné vrátiť späť.',
    },
    confirmRemove: {
      header: 'Odobrať "{item}"?',
    },
    takesEffectOnSave: 'Zmena sa prejaví po uložení.',
    confirmSaveChanges: {
      header: 'Uložiť zmeny v "{item}"?',
      message: 'Uložená verzia sa prepíše.',
    },
    confirmChangeValue: {
      header: 'Zmeniť hodnotu na "{value}"?',
      message: 'Aktuálna hodnota je "{currentValue}".',
    },
    remove: 'Odobrať',
    selectAll: 'Vybrať všetko',
    selectItem: 'Vybrať {item}',
    deselectAll: 'Zrušiť výber všetkých',
    value: 'Hodnota | Hodnoty',
    validate: 'Overiť',
    selectEquipment: 'Vyberte zariadenie z navigácie vľavo pre zobrazenie podrobností',
    required: 'Povinné',
    new: 'Nowy',
    calendarWeekAbbreviation: 'KT',
    unlink: 'Odpojiť',
    other: 'Iné',
    cancel: 'Zrušiť',
    error: 'Chyba',
    schedule: 'Plán',
    always: 'Vždy',
    asset: 'Zariadenie',
    assets: 'Zariadenia',
    severityLevels: 'Úrovne závažnosti',
    history: 'História',
    restore: 'Obnoviť',
    errorMessage: {
      errorSignalLoading: 'Chyba načítania {displayName}',
      noAccess: '{entity} nemá prístup "{accessType}" k {item}',
      default: 'Niečo sa pokazilo, obnovte stránku. Ak chyba pretrváva, kontaktujte podporu.',
      pt9800: {
        AxiosError401: 'Mapovanie používateľa nebolo vykonané alebo je neplatné',
        AxiosError401Item: 'Mapovanie používateľa nebolo vykonané alebo je neplatné pre {item}',
        AxiosError403: 'Používateľ nemá požadované oprávnenia na prístup k údajom Protherm',
        AxiosError403Item: 'Používateľ nemá požadované oprávnenia na prístup k údajom Protherm pre {item}',
      },
    },
    fileGeneration: {
      summary: 'Generovanie súboru',
      detail: 'Generovanie súboru môže chvíľu trvať. Ponechajte okno prehliadača otvorené. Táto správa sa zatvorí po dokončení požiadavky.',
    },
    update: 'Aktualizovať',
    create: 'Vytvoriť',
    createCopy: 'Vytvoriť kópiu',
    deleteItem: 'Odstrániť {item}',
    editItem: 'Upraviť {item}',
    saveItem: 'Uložiť {item}',
    saveAsNew: 'Uložiť ako...',
    export: 'Exportovať',
    import: 'Importovať',
    search: 'Hľadať',
    selected: 'Vybraté',
    back: 'Späť',
    forward: 'Dopredu',
    accept: 'Prijať',
    submit: 'Potvrdiť',
    success: 'Úspech',
    close: 'Zavrieť',
    week: {
      [EWeekday.MONDAY]: 'Pondelok',
      [EWeekday.TUESDAY]: 'Utorok',
      [EWeekday.WEDNESDAY]: 'Streda',
      [EWeekday.THURSDAY]: 'Štvrtok',
      [EWeekday.FRIDAY]: 'Piatok',
      [EWeekday.SATURDAY]: 'Sobota',
      [EWeekday.SUNDAY]: 'Nedeľa',
    },
    month: {
      [EMonth.JANUARY]: {
        long: 'Január',
        short: 'Jan',
      },
      [EMonth.FEBRUARY]: {
        long: 'Február',
        short: 'Feb',
      },
      [EMonth.MARCH]: {
        long: 'Marec',
        short: 'Mar',
      },
      [EMonth.APRIL]: {
        long: 'Apríl',
        short: 'Apr',
      },
      [EMonth.MAY]: {
        long: 'Máj',
        short: 'Máj',
      },
      [EMonth.JUNE]: {
        long: 'Jún',
        short: 'Jún',
      },
      [EMonth.JULY]: {
        long: 'Júl',
        short: 'Júl',
      },
      [EMonth.AUGUST]: {
        long: 'August',
        short: 'Aug',
      },
      [EMonth.SEPTEMBER]: {
        long: 'September',
        short: 'Sep',
      },
      [EMonth.OCTOBER]: {
        long: 'Október',
        short: 'Okt',
      },
      [EMonth.NOVEMBER]: {
        long: 'November',
        short: 'Nov',
      },
      [EMonth.DECEMBER]: {
        long: 'December',
        short: 'Dec',
      },
    },
    timeAbbreviation: {
      days: 'dni',
      hours: 'hod.',
      minutes: 'min',
      months: 'mes.',
      weeks: 'týž.',
      years: 'rok.',
    },
    reset: 'Obnoviť',
    none: 'Žiadne',
    copy: 'Kopírovať',
    copyNoun: 'Kópia',
    time: {
      second: 'Sekunda | Sekundy',
      minute: 'Minúta | Minúty',
      hour: 'Hodina | Hodiny',
      day: 'Deň | Dni',
      weekday: 'Pracovný deň | Pracovné dni',
      week: 'Týždeň | Týždne',
      month: 'Mesiac | Mesiace',
      quarter: 'Štvrťrok | Štvrťroky',
      year: 'Rok | Roky',
    },
    task: 'Úloha | Úlohy',
    read: 'Čítať',
    write: 'Písať',
  },
  component: {
    accordionAudit: {
      envelopeNamePlaceholder: 'Názov obálky',
    },
    alarmView: {
      header: 'Alarmy',
      empty: 'Nenašli sa žiadne alarmy',
    },
    appAndAssetPermissionTree: {
      appPermission: 'Oprávnenia aplikácie',
      assetPermission: 'Oprávnenia aktív',
    },
    counterActionModal: {
      create: 'Vytvoriť protiopatrenie',
      update: 'Aktualizovať protiopatrenie',
    },
    counterActionTable: {
      create: 'Vytvoriť protiopatrenie',
      confirmDelete: 'Odstrániť protiopatrenie „{counterActionName}"?',
      confirmUnlink: 'Odpojiť príčinu „{rootCauseName}" od protiopatrenia „{counterActionName}"?',
    },
    dashboardPanel: {
      add: 'Pridať panel',
      defaultTitle: 'Môj panel',
    },
    furnaceViewRecipe: {
      stopConfirm: {
        header: 'Zastaviť recept "{0}"?',
        message: 'Recept sa okamžite zastaví.',
      },
      startConfirm: {
        header: 'Spustiť recept "{0}"?',
        message: 'Recept sa okamžite spustí.',
      },
      goToConfirm: {
        message: 'Recept okamžite pokračuje týmto krokom.',
      },
    },
    expressionPreviewModal: {
      title: 'Nový virtuálny signál',
    },
    equipmentDocumentUploadModal: {
      headline: 'Nahrať dokumenty',
      categoryTitle: 'Kategória',
      descriptionTitle: 'Popis',
      uploadFilesTitle: 'Súbor',
      errorMessage: 'Súbor už existuje',
    },
    loadModal: {
      startingLoad: 'Job sa spúšťa...',
      confirmPartHint: 'Potvrďte, že všetky časti sú načítané',
      confirmStart: 'Potvrďte spustenie jobu.',
      loadIdentifier: generalTerm.loadIdentifier,
      createOrUpdate: 'Naplánovať job',
      description: 'Naplánujte ďalšie načítanie pridaním vašich',
      addTraveller: 'Pridať nového cestovného sprievodcu',
      emptyPartsTable: 'Neboli pridané žiadne časti',
      partNotFound: 'Časť s ID "{id}" nebola nájdená',
      partDoNotMatch: 'Nie všetky časti zodpovedajú receptúre',
      readyToScan: 'Pripravený na skenovanie',
    },
    materialSelect: {
      placeholder: 'Vybrať materiál',
    },
    materialGroupTable: {
      tableHeader: 'Skupiny materiálov',
    },
    maintenanceCalendarJobModal: {
      titleCreate: 'Vytvoriť údržbový job',
      titleEdit: 'Upraviť údržbový job',
      createTask: 'Vytvoriť úlohu',
    },
    observationView: {
      header: 'Pozorovania',
    },
    observationTypeTable: {
      unlinkConfirm: 'Odpojiť príčinu „{rootCauseName}" od typu pozorovania „{observationTypeName}"?',
    },
    PartAttribute: {
      headline: 'Nastavenia dielov',
      subheadline: 'Atribúty definované v tejto tabuľke budú požadované na vyplnenie pri vytváraní/úprave dielov.',
    },
    productionLogsTable: {
      tab: {
        table: 'Tabuľka',
        chart: 'Graf',
      },
    },
    qualityForm: {
      jobSelection: 'Výber vsázky',
      partSelection: 'Výber dielu',
    },
    rootCauseLinkModal: {
      title: 'Prepojiť príčinu',
    },
    rootCauseModal: {
      create: 'Vytvoriť príčinu',
      update: 'Aktualizovať príčinu',
    },
    rootCauseGroupModal: {
      create: 'Vytvoriť skupinu príčin',
      update: 'Aktualizovať skupinu príčin',
    },
    rootCauseTable: {
      create: 'Vytvoriť príčinu',
      confirmDelete: 'Odstrániť príčinu „{rootCauseName}"?',
      confirmUnlink: 'Odpojiť protiopatrenie „{counterActionName}" od príčiny „{rootCauseName}"?',
    },
    rootCauseGroupTable: {
      create: 'Vytvoriť skupinu príčin',
      confirmDelete: 'Odstrániť skupinu príčin „{rootCauseGroupName}"?',
      confirmUnlink: 'Odpojiť príčinu „{rootCauseName}" od skupiny príčin „{rootCauseGroupName}"?',
    },
    TableJobStages: {
      header: 'Etapy receptu',
    },
    treeSidebarTemplate: {
      defaultTitle: 'Navigácia aktív',
    },
    userUpdateModal: {
      tab: {
        permissionList: 'Oprávnenia',
        controllerUserMapping: 'Mapovanie používateľa kontroléra',
      },
      permissionList: 'Zoznam oprávnení',
      groupList: 'Zoznam skupín',
      selectPT9800: 'Vybrať používateľa Protherm',
      enterCredentials: 'Zadať prihlasovacie údaje',
    },
  },
  details: {
    images: 'Obrázky',
  },
  errorMessages: {
    error: 'Chyba',
    noEquipmentSelected: 'Ak chcete zobraziť podrobnosti, vyberte zariadenie v navigácii zariadení na ľavej strane',
    outOfRangeTemperature: 'Teplota je mimo rozsahu',
  },
  forms: {
    credentials: 'Prihlasovacie údaje',
    persistCredentials: 'Uložiť prihlasovacie údaje',
  },
  global: {
    workplace: 'Pracovisko',
  },
  images: {
    noImages: 'Žiadne obrázky',
  },
  inputLabels: {
    from: 'od',
    to: 'do',
  },
  materialComposition: {
    min: {
      label: 'Min',
      description: 'Analýza s najmenšími vlastnosťami materiálu.',
    },
    max: {
      label: 'Max',
      description: 'Analýza s najväčšími vlastnosťami materiálu.',
    },
    avg: {
      label: 'Priemer',
      description: 'Analýza s priemernými vlastnosťami materiálu.',
    },
    comp: {
      label: 'Priemerná analýza',
      description: 'Priemerná analýza podľa hmotnostných percent.',
    },
  },
  modifyMenu: {
    addNew: 'Nový {0}',
    edit: 'Upraviť',
    saveAs: 'Uložiť ako',
  },
  page: {
    anomalyDetection: {
      noObservations: 'Nenašli sa žiadne pozorovania',
    },
    certification: {
      title: 'Audit a kvalita',
      auditConfiguration: {
        deleteSuccess: 'Špecifikácia bola úspešne odstránená.',
      },
    },
    openSourceLicenses: {
      title: 'Licencie open source',
    },
    liveApp: {
      equipment: {
        noJobs: 'Nenašli sa žiadne vsázky',
      },
    },
    notification: {
      title: 'Oznámenia',
    },
    userManagement: {
      manageRole: 'Spravovať roly',
      headline: 'Používatelia ({0})',
      customPermissions: 'Vlastné oprávnenia',
      searchPlaceholder: {
        name: 'Vyhľadať podľa názvu',
        email: 'Vyhľadať podľa e-mailu',
        role: 'Vyhľadať podľa roly',
      },
      showDeletedUsers: 'Zobraziť odstránených používateľov',
      restoreUser: 'Obnoviť používateľa',
      confirmRestoreUser: {
        header: 'Obnoviť "{displayName}"?',
        message: 'Používateľ sa bude môcť znova prihlásiť do Qmulus.',
      },
      confirmDeleteUserMessage: 'Odstránených používateľov možno obnoviť.',
      deleted: 'Odstránený',
    },
    roleManagement: {
      roles: 'Roly',
      createRole: 'Vytvoriť rolu',
    },
    home: {
      title: 'Domovská stránka',
      welcomeMorning: 'Dobré ráno, {firstName}.',
      welcomeAfternoon: 'Dobrý deň, {firstName}.',
      welcomeEvening: 'Dobrý večer, {firstName}.',
      welcomeNight: 'Ešte stále v práci, {firstName}?',
      welcomeSubtitles: [
        'Vaše pece čakajú — urobme z dnešného dňa skvelý beh.',
        'Čas rozohriať deň — vaše procesy sú pripravené.',
        'Uistite sa, že každý zákazka dnes prebehne podľa špecifikácie.',
        'Sledujte svoje lokality, monitorujte zákazky a udržujte kontrolu.',
        'Všetky vaše operácie tepelného spracovania vždy po ruke.',
        'Váš prehľad každej lokality, každej pece, každej zákazky.',
        'Čo dnes beží? Prejdite do prehľadu aplikácií a zistite.',
        'Majte svoje vsádzky pod kontrolou a dbajte o kvalitu.',
        'Skontrolujte svoje pece a udržujte procesy na správnom kurze.',
      ],
    },
    appOverview: {
      title: 'Prehľad aplikácií',
      showOnlyAvailableApps: 'Zobraziť iba dostupné aplikácie',
      search: 'Hľadať aplikáciu',
    },
    releaseNotes: {
      title: 'Poznámky k vydaniu',
    },
    releaseHighlights: {
      title: 'Novinky',
    },
    userManual: {
      title: 'Používateľská príručka',
    },
    productionStatisticsDetails: {
      panelSettings: {
        header: 'Nastavenia panelu',
        helpText: 'Vyberte názov panelu a vyberte KPI, ktoré sa majú zobrazovať. Typ grafu je možné vybrať na paneli.',
        labelTitle: 'Názov',
        labelSize: 'Veľkosť',
        labelType: 'Typ',
        labelKpiSelection: 'Výber KPI',
      },
      size: {
        medium: 'Średni',
        large: 'Veľký',
      },
      type: {
        table: 'Tabela',
        totals: 'Celkové',
        totalDistribution: 'Celkové rozdelenie',
        childrenTotals: 'Súčty podriadených',
        childrenDistribution: 'Rozdelenie podriadených',
        childrenComparison: 'Porovnanie podriadených',
      },
    },
    assetManagementEquipmentSensorMapping: {
      mappedSignalTitle: 'Namapovaný signál',
    },
    assetManagement: {
      overview: {
        title: 'Prehľad aktív',
        tableHeader: 'Strom aktív',
        filter: {
          name: 'Filtrovať podľa názvu',
        },
      },
      equipmentTab: {
        [ERouteName.ASSET_MANAGEMENT_ALARM_MANAGEMENT]: 'Správa alarmov',
        [ERouteName.ASSET_MANAGEMENT_CAMERAS]: 'Kamery',
        [ERouteName.ASSET_MANAGEMENT_DATA_SOURCES]: 'Zdroje dát',
        [ERouteName.ASSET_MANAGEMENT_DOCUMENT]: 'Dokumenty',
        [ERouteName.ASSET_MANAGEMENT_MAIN]: 'Prehľad',
        [ERouteName.ASSET_MANAGEMENT_PARAMETER]: 'Parametre',
        [ERouteName.ASSET_MANAGEMENT_SENSOR_MAPPING]: 'Mapovanie senzorov',
        [ERouteName.ASSET_MANAGEMENT_SENSOR_SETTINGS]: 'Nastavenia senzorov',
      },
    },
    pt9800: {
      common: {
        id: 'ID',
        name: 'Názov',
        description: 'Popis',
        modifiedBy: 'Zmenené',
        modifiedOn: 'Zmenené dňa',
        number: 'Číslo',
        comment: 'Komentár',
      },
      confirmDeleteHeader: 'Odstrániť {0} "{1}"?',
      error: {
        create: 'Nepodarilo sa vytvoriť {0}',
        update: 'Nepodarilo sa aktualizovať {0}',
        delete: 'Nepodarilo sa odstrániť {0}',
        clone: 'Nepodarilo sa klonovať {0}',
      },
      form: {
        header: {
          create: 'Vytvoriť {0}',
          edit: 'Upraviť {0}',
          delete: 'Odstrániť {0}',
        },
      },
      entryType: {
        alarm: 'Alarm',
        application: 'Aplikácia',
        applicationGroup: 'Skupina aplikácií',
        box: 'Box',
        clientAccess: 'Prístup klienta',
        color: 'Farba',
        terminal: 'Terminál',
        terminalType: 'Typ terminálu',
        user: 'Používateľ',
        userAccess: 'Prístup používateľa',
        userGroup: 'Skupina používateľov',
        backgroundProcess: 'Proces na pozadí',
        tasks: 'Úlohy',
        workplace: 'Pracovisko',
        workplaceType: 'Typ pracoviska',
        operation: 'Operácia',
      },
      backgroundProcess: {
        order: 'Zákazka',
        parameter: 'Parameter',
        active: 'Aktívny',
        activeTime: 'Aktívny čas',
        pid: 'PID',
        header: 'Proces na pozadí',
      },
      operation: {
        header: 'Operácia',
        property: 'Vlastnosť',
        operationType: 'Typ operácie',
        type: 'Typ',
        mode: 'Režim',
        order: 'Zákazka',
        peProcessType: 'Typ procesu PE',
        sortingNo: 'Poradie zoradenia',
        automatingPlanningMode: 'Režim automatického plánovania',
        relevantForProductionStatistics: 'Relevantné pre výrobné štatistiky',
        relevantForStat: 'Relevantné pre štatistiku',
        selectProcess: 'Vybrať proces',
        selectPart: 'Vybrať diel',
        sapNo: 'SAP č.',
        workplaceName: 'Pracovisko/Pracovné skupiny',
        placeDescription: 'Popis miesta',
      },
      tasks: {
        header: 'Správa úloh',
        active: 'Aktívny',
        type: 'Typ',
        command1: 'Príkaz 1',
        command2: 'Príkaz 2',
        command3: 'Príkaz 3',
        command4: 'Príkaz 4',
        command5: 'Príkaz 5',
        command6: 'Príkaz 6',
        command7: 'Príkaz 7',
        command8: 'Príkaz 8',
        command9: 'Príkaz 9',
        command10: 'Príkaz 10',
        startHour: 'Hodina začiatku',
        dateLastActive: 'Dátum poslednej aktivity',
        intervalInMin: 'Interval v min [min]',
      },
      workplace: {
        logicTypes: 'Typy logiky',
        header: 'Pracovisko',
        inventoryNumber: 'Inventárne číslo',
        belongsTo: 'Patrí k ID pracoviska',
        ip: 'IP adresa',
        mGuardIp: 'MGuard IP',
        device: 'Zariadenie',
        telegramSplit: 'Rozdelenie telegramov RFC',
        deactivateOperations: 'Deaktivovať operácie',
        aliveBitErrorNum: 'Číslo chyby aktívneho bitu',
        logicType: 'Typ logiky',
        recipePrefix: 'Prefix receptu',
        chamberId: 'ID komory',
        sharesController: 'Zdieľaný radič/kópia ID komory',
        mainUseMode: 'Hlavný režim používania',
        workplaceType: 'Typ miejsca pracy',
        isActive: 'Je aktívny',
        importantSettings: 'Dôležité nastavenia',
      },
      alarm: {
        alarmAdministration: 'Správa alarmov',
        alarm: 'Alarm',
        alarmText: 'Text alarmu',
        causesTestingLoad: 'Spôsobuje testovaciu vsádzku',
        onlyForChamber: 'Iba pre komoru',
        security: 'Bezpečnosť',
        color: 'Kolor',
        errorType: 'Typ chyby',
        outputType: 'Typ výstupu',
        alarmGroup: 'Skupina alarmov',
      },
      application: {
        header: 'Aplikácia',
        command: 'Príkaz',
        groupId: 'Skupina',
        iconGifName: 'Ikona-Názov-GIF',
        furnacePicture: 'Obrázok pece',
        order: 'Objednávka',
        parameter: 'Parametr',
        tooltip: 'Popis',
        typeId: 'Typ',
      },
      applicationGroup: {
        header: 'Skupina aplikácií',
      },
      systemData: {
        active: 'Aktívny',
        addresseeMobilePhoneNo: 'Číslo mobilného telefónu adresáta',
        boxHeader: 'Správa boxov',
        colorHeader: 'Správa farieb',
        colorValue: 'Hodnota farby',
        pin: 'PIN',
        serialInterface: 'Sériové rozhranie',
        smsHeader: 'SMS',
        testMessage: 'Testovacia správa (1=ÁNO)',
        used: 'Používaný',
      },
      terminal: {
        header: 'Terminál',
        id: 'Id',
        clientType: 'Typ terminálu',
        name: 'Názov',
        osType: 'Operačný systém',
        remark: 'Poznámka',
        address: 'Adresa',
        description: 'Opis',
        location: 'Miesto',
        valueLong2: 'Hodnota INT 2',
        valueLong3: 'Hodnota INT 3',
        valueLong4: 'Hodnota INT 4',
        valueLong5: 'Hodnota INT 5',
        valueChar1: 'Hodnota znaku 1',
        valueChar2: 'Hodnota znaku 2',
        actUserName: 'Aktuálny používateľ',
        actLoginTime: 'Prihlásený od',
        terminalNo: 'Číslo terminálu',
        lastLoginTime: 'Posledný čas prihlásenia',
        lastUserName: 'Posledný používateľ',
        resolution: 'Rozlíšenie obrazovky',
        permission: 'Oprávnenie',
        rightsToTerminals: 'Práva k terminálom',
      },
      terminalType: {
        header: 'Typ terminálu',
      },
      user: {
        header: 'Používateľ',
        accessLevel: 'Úroveň prístupu používateľa',
        comment: 'Komentár',
        costCenter: 'Nákladové stredisko',
        employeeNumber: 'Číslo zamestnanca',
        shortName: 'Krátky názov',
        firstName: 'Krstné meno',
        function: 'Funkcia',
        lastName: 'Priezvisko',
        language: 'Jazyk',
        loginTimeInMin: 'Čas prihlásenia v minútach.',
        maintenancePopups: 'Okná plánu údržby',
        password: 'Heslo',
        plant: 'Závod',
        playAlarmSound: 'Prehrať zvuk alarmu pri alarmoch',
        userGroup: 'Skupina používateľov',
      },
      userAccess: {
        header: 'Prístup používateľa',
        delete: 'Odstrániť',
        newEntry: 'Nový záznam',
        print: 'Tlačiť',
        run: 'Spustiť',
        save: 'Uložiť',
        special1: 'Špeciálny-1',
        startRecipeProcessFlow: 'Spustiť recept/priebeh procesu',
        modifyRecipeProcessFlow: 'Upraviť recept/priebeh procesu',
        startCorrectionRecipe: 'Spustiť korekčný recept',
        stopRecipeProcessFlow: 'Zastaviť recept/priebeh procesu',
        selectUser: 'Vybrať používateľa',
      },
      userGroup: {
        header: 'Skupina používateľov',
      },
      workplaceType: {
        header: 'Typ pracoviska',
        prio: 'Priorita',
      },
    },
    login: {
      title: 'Prihlásenie',
    },
    anomalyDetectionJobFeatureDetail: {
      tab: {
        observationTable: 'Pozorovania',
        rootCauseTable: 'Koreňové príčiny',
      },
    },
    anomalyDetectionEquipmentSettings: {
      title: 'Ustawienia',
      tab: {
        counterActionTable: 'Protiakcie',
        rootCauseGroupTable: 'Skupina koreňových príčin',
        rootCauseTable: 'Koreňové príčiny',
        jobFeatureTable: 'Funkcie vsádzky',
      },
    },
    anomalyDetectionEquipment: {
      tab: {
        rootCauseTable: 'Možné koreňové príčiny',
        jobFeatureTable: 'Pozorovania zoskupené podľa funkcií vsádzky',
      },
      jobFeatureTable: {
        header: {
          jobFeatureName: 'Funkcia vsádzky',
          observationCount: 'Počet pozorovaní',
          rootCauseCount: 'Počet koreňových príčin',
        },
      },
      observationTypeByJobFeatureTable: {
        header: {
          observationTypeName: 'Názov typu pozorovania',
          observationCount: 'Počet pozorovaní',
          rootCauseIdList: 'Počet koreňových príčin',
        },
      },
    },
    quality: {
      header: {
        title: 'Kvalita',
        createReport: 'Vytvoriť správu',
      },
    },
    auditResult: {
      asset: 'Zariadenie',
      userOverruledReason: 'Dôvod prepísania používateľom',
      duration: 'Trvanie',
      endTime: 'Čas ukončenia',
      headerTitle: 'Správa auditu',
      loadNo: 'Č. vsádzky',
      location: 'Lokalita',
      rating: 'Hodnotenie',
      recipe: 'Recept',
      reportNo: 'Číslo správy',
      reporter: 'Reporter',
      signature: {
        customer: 'Zákazník',
        engineering: 'Inžinierstvo',
        manufacturing: 'Výroba',
        material: 'Materiál',
        program: 'Program',
        quality: 'Kvalita',
      },
      startTime: 'Čas začatia',
    },
    productionLogList: {
      compare: 'Porovnať',
      export: 'Export',
    },
  },
  recipeBuilder: {
    alarmDeactivated: 'Alarm deaktivovaný',
    alarmDelay: 'Oneskorenie alarmu',
    equi: 'Rovnaký',
    highAlarmOffsets: 'Vysoké odchylky alarmu',
    highWarningOffsets: 'VYSOKÉ odchylky varovania',
    lowAlarmOffsets: 'NÍZKE odchylky alarmu',
    lowWarningOffsets: 'NÍZKE odchylky varovania',
    referenceValue: 'Referenčná hodnota',
    setAlarmLimit: 'Nastaviť limit alarmu',
    setAlarmLimits: 'Nastaviť limity alarmov',
    setValue: 'Nastaviť hodnotu',
  },
  recipeOnDemand: {
    chart: {
      gasCompositionLabel: {
        ammonia: 'Amoniak',
        dissociatedAmmonia: 'Disociovaný amoniak',
        nitrogen: 'Dusík',
        carbonOxide: 'Oxid uhoľnatý',
        carbonDioxide: 'Oxid uhličitý',
      },
      processLabel: {
        knAct: 'Kn skutočný',
      },
    },
    compoundLayerThickness: 'Hrúbka zlúčeninovej vrstvy',
    coreHardnessHV: 'Tvrdosť jadra (HV)',
    diffusionLayerThickness: 'Hrúbka difúznej vrstvy',
    distance: 'Vzdialenosť',
    effective: 'Účinný',
    gasFlowChart: 'Graf prietoku plynu',
    hardness: 'Tvrdosť',
    material: 'Materiál',
    nitridetHardness: 'Hĺbka tvrdosti nitridovanej vrstvy',
    nitriding: 'Nitridovanie',
    nitrocarburizing: 'Nitrokarburácia',
    oxideLayerThickness: 'Hrúbka oxidovej vrstvy',
    porosity: 'Pórovitosť %',
    postoxidation: 'Postoxidácia',
    process: 'Proces',
    processChart: 'Graf procesu',
    profileChart: 'Graf profilu',
    stepNumber: 'Krok {number}',
    surfaceHardness: 'Povrchová tvrdosť',
    unit: 'Jednostka',
    unitSystems: {
      imperial: 'Imperiálny',
      metric: 'Metrický',
    },
    vickersHardness: 'Vickersova tvrdosť',
  },
  recipes: {
    carburizingDepth: generalTerm.carburizingDepth,
    endTemperature: 'Záverečná teplota',
    grainSize: 'Číslo veľkosti zrna ASTM',
    nitridingDepth: generalTerm.nitridingDepth,
    quenchingIntensity: 'Intenzita kalenia',
    representativeDiameter: 'Reprezentatívny priemer',
    requiredHardnessHV: 'Požadovaná tvrdosť',
    noAccess: 'Používateľ nemá prístup k {0}',
  },
  report: {
    approvals: 'Schválenia',
    customer: 'Zákazník',
    engineering: 'Inžinierstvo',
    manufacturing: 'Výroba',
    materiel: 'Materiál',
    program: 'Program',
    quality: 'Kvalita',
  },
  simulator: {
    overwrite: 'Prepísať',
  },
  tableHeaders: {
    alarmType: 'Typ alarmu',
    avg: 'Priemer {unit}',
    description: 'Popis',
    max: 'Max {unit}',
    min: 'Min {unit}',
    name: 'Názov',
    value: 'Hodnota',
    valueWhenDeclared: 'Hodnota pri spustení',
    timeOfDeclaration: 'Čas spustenia',
  },
  tabs: {
    materials: 'Materiály',
    materialsGroups: 'Skupiny materiálov',
  },
  tiles: {
    userManagement: 'Správa používateľov',
    materials: 'Materiály',
    part: 'Diely',
    loadingScheme: 'Schémy vsádzky',
    productionLogs: 'Protokoly výroby',
    customer: 'Klient',
  },
  titles: {
    alarms: 'Alarmy',
    noData: 'Žiadne dáta',
    settings: 'Nastavenia',
    valuesAndCalculations: 'Hodnoty a výpočty',
  },
  type: {
    Visualisation,
  },
  upcPro: {
    analyzerReading: 'Odčítanie analyzátora',
    convertsTo: 'konvertuje na',
    endoGeneratorDataError: 'CHYBA ZADÁVANIA DÁT',
    floMeterGlossary: 'Glosár pojmov',
    inputGroup: 'Vstup',
    flows: 'Vstupné toky',
    furnaceAtmosphere: 'Atmosféra pece',
    imperialUnits: 'imperiálny',
    metricUnits: 'metrický',
    potentials: 'Potenciály',
    inputs: {
      CELSIUS_TO_FAHRENHEIT: 'Celsius na Fahrenheit',
      FAHRENHEIT_TO_CELSIUS: 'Fahrenheit na Celsius',
      FT3PH_TO_M3PH: 'cfh na m³/h',
      GPM_TO_LMIN: 'US gpm na l/min',
      HARDNESS_TABLE1: 'Rozsah Rockwell C',
      HARDNESS_TABLE2: 'Rozsah Rockwell B',
      HBS: {
        name: 'HBS',
        subtitle: 'Brinellova stupnica',
      },
      HBS2: {
        name: 'HBS',
        subtitle: 'Skala Brinella',
      },
      HBW: {
        name: 'HBW',
        subtitle: 'Skala Brinella',
      },
      HK: {
        name: 'HK',
        subtitle: 'Knoop tvrdosť',
      },
      HK2: {
        name: 'HK',
        subtitle: 'Tvrdosť Knoopa',
      },
      HR15N: {
        name: 'HR15N',
        subtitle: 'Stupnica Rockwell 15-N',
      },
      HR15T: {
        name: 'HR15T',
        subtitle: 'Stupnica Rockwell 15-T',
      },
      HR30N: {
        name: 'HR30N',
        subtitle: 'Stupnica Rockwell 30-N',
      },
      HR30T: {
        name: 'HR30T',
        subtitle: 'Stupnica Rockwell 30-T',
      },
      HR45N: {
        name: 'HR45N',
        subtitle: 'Stupnica Rockwell 45-N',
      },
      HR45T: {
        name: 'HR45T',
        subtitle: 'Stupnica Rockwell 45-T',
      },
      HRA: {
        name: 'HRA',
        subtitle: 'Stupnica Rockwell A',
      },
      HRA2: {
        name: 'HRA',
        subtitle: 'Stupnica Rockwell A',
      },
      HRB: {
        name: 'HRB',
        subtitle: 'Rockwell B tvrdosť',
      },
      HRC: {
        name: 'HRC',
        subtitle: 'Rockwell C tvrdosť',
      },
      HRD: {
        name: 'HRD',
        subtitle: 'Stupnica Rockwell D',
      },
      HRF: {
        name: 'HRF',
        subtitle: 'Rockwell F tvrdosť',
      },
      HSC: {
        name: 'HSc',
        subtitle: 'Skleroskopická tvrdosť HSc',
      },
      HV: {
        name: 'HV',
        subtitle: 'Vickersova tvrdosť',
      },
      HV2: {
        name: 'HV',
        subtitle: 'Vickerova tvrdosť',
      },
      LMIN_TO_GPM: 'l/min na US gal/min',
      M3PH_TO_FT3PH: 'm³/h na cfh',
      ac: 'ac',
      actualScale: 'Skutočná stupnica',
      aisi: 'AISI',
      al: 'Glin',
      alloyFactor: 'Zliatinový faktor',
      baseC: 'Základ C',
      c: 'C',
      carbonPotential: 'Uhlíkový potenciál',
      cch4: 'C + CH₄',
      ccontent: 'Obsah C',
      ch3oh: 'CH₃OH',
      ch4: 'CH₄',
      ch4Factor: 'Faktor CH₄',
      co: 'CO',
      co2: 'CO₂',
      coTotalFlow: 'CO/Celkový prietok',
      coco2: 'CO/CO₂',
      coldJunctionTemp: 'Teplota studeného spoja.',
      cpatm: 'CPatm',
      cr: 'Kr',
      cu: 'Cu',
      dewPoint: 'Rosný bod',
      din: 'DIN',
      dissBurette: 'Rozšírená byreta',
      dissDegree: 'Stupeň disociácie',
      dnh3: 'D-NH₃',
      dpt: 'DPT',
      emf: 'EMF',
      floMeterScale: 'Stupnica Flo-metra',
      flow: 'Prietok',
      gas: 'Gaz',
      h2: 'H₂',
      h2n2: 'H₂',
      h2o: 'H₂O',
      hardness: 'Tvrdosť',
      kcb: 'Kcʙ',
      kh: 'Kʜ',
      kn: 'Kɴ',
      ko: 'Ko',
      mn: 'Mn',
      mo: 'Mo',
      mv: 'mV',
      n2: 'N₂',
      n2Percentage: 'N₂',
      n2ch3oh: 'N₂/CH₃OH',
      nc2: 'NC₂',
      nh3: 'NH₃',
      ni: 'Ni',
      o2: 'O₂',
      o2Percentage: 'O₂',
      o2Probe: 'Sonda O₂',
      others: 'Iné',
      p1: 'P1',
      p2: 'P2',
      po2: 'pO₂',
      preload: 'Predúčtovanie',
      pt10: {
        description: '(Pt10) Platynowy czujnik RTD o rezystancji nominalnej 10 omów w temperaturze 0°C (32°F).\nZakres',
        name: '(Pt10) Platyna RTD',
      },
      pt100: {
        description: '(Pt100) Platynowy czujnik RTD o rezystancji nominalnej 100 omów w temperaturze 0°C (32°F).\nZakres',
        name: '(Pt100) Platynowy czujnik RTD',
      },
      pt1000: {
        description: '(Pt1000) Platynowy czujnik RTD o rezystancji nominalnej 1000 omów w temperaturze 0°C (32°F).\nZakres',
        name: '(Pt1000) Platynowy czujnik RTD',
      },
      pt500: {
        description: '(Pt500) Platynowy czujnik RTD o rezystancji nominalnej 500 omów w temperaturze 0°C (32°F).\nZakres',
        name: '(Pt500) Platynowy czujnik RTD',
      },
      resistance: 'Odpor',
      rg: 'RG',
      rl: 'RL',
      scaleFactor: 'Faktor stupnice',
      sg1: 'SG1',
      sg2: 'SG2',
      si: 'Si',
      sootLimit: 'Limit sadzí',
      tc_b: {
        description: 'Plus: Platyna 30% rodu (Pt30Rh)\nMinus: Platyna 6% rodu (Pt6Rh)\nZakres: -50°C–1820°C (-58°F–3308°F)\nKolory zgodne z normą ANSI 96.6.\nZgodnie z normą NFC 42-324, kolory to żółty i szary',
        name: 'Typ B = Pt30Rh-Pt6Rh',
      },
      tc_e: {
        description: 'Plus: Nikiel-Chrom (NiCr), znany również jako Chromel\nMinus: Miedź-Nikiel (CuNi), znany również jako Konstantan\nZakres: -270°C-1000°C (-454°F-1832°F)\\Kolory zgodne z normą DIN EN 60584-1',
        name: 'Typ E = NiCr-CuNi',
      },
      tc_j: {
        description: 'Plus: Miedź (Cu), Kolor miedzi\nMinus: Miedź-nikiel (CuNi), znany również jako Konstantan\nZakres: -270°C–400°C (-454°F–752°F)\\Kolory zgodne z normą DIN EN 60584-1',
        name: 'Typ J = Fe-CuNi',
      },
      tc_k: {
        description: 'Plus: Nikiel-chrom (NiCr), znany również jako Chromel\nMinus: Nikiel-aluminium (NiLe), znany również jako Alumel, drut magnetyczny\nZakres: -270°C-1372°C (-454°F-2502°F)\\Kolory zgodnie z normą DIN EN 60584-1',
        name: 'Typ K = NiCr-Ni',
      },
      tc_l: {
        description: 'Plus: Żelazo (Fe), drut magnetyczny\nMinus: Miedź-nikiel (CuNi), znany również jako Konstantan\nZakres temperatur: 0°C–900°C (32°F–1652°F)\\Kolory zgodnie z normą DIN EN 43714',
        name: 'Typ L = Fe-CuNi',
      },
      tc_n: {
        description: 'Plus:Nikiel-Chrom-Krzem (NiCrSi),\nznany również jako Nicrosil\nMinus:Nikiel-Krzem (NiSi),\nznany również jako Nisil\nZakres: -270°C-1300°C (-454°F-2372°F)\nKolory zgodne z normą DIN EN 60584-1',
        name: 'Typ N = NiCrSi-NiSi',
      },
      tc_r: {
        description: 'Plus: Platyna 13% rodu (Pt13Rh)\\Minus: Platyna (Pt), drut ujemny bardziej miękki\nniž drut dodatni\nZakres: -50°C–1768°C (-58°F–3214°F)\\Kolory zgodnie z normą DIN EN 60584-1',
        name: 'Typ R = Pt13Rh-Pt',
      },
      tc_s: {
        description: 'Plus: Platyna 10% rod (Pt 10 Rh)\\Minus: Platyna (Pt), drut ujemny bardziej miękki\nniž drut dodatni\nZakres: -50°C–1768°C (-58°F–3214°F)\\Kolory zgodnie z normą DIN EN 60584-1',
        name: 'Typ S = Pt10Rh-Pt',
      },
      tc_t: {
        description: 'Plus: Żelazo (Fe), drut magnetyczny\nMinus: Miedź-nikiel (CuNi), znany również jako Konstantan\nZakres: -210°C–1200°C (-346°F–2192°F)\\Kolory zgodnie z normą DIN EN 60584-1',
        name: 'Typ T = Cu-CuNi',
      },
      tc_u: {
        description: 'Plus: Miedź (Cu), Kolor miedzi\nMinus: Miedź-nikiel (CuNi), znany również jako Konstantan\nZakres: 0°C–600°C (32°F–1112°F)\\Kolory zgodne z normą DIN EN 43714',
        name: 'Typ U = Cu-CuNi',
      },
      temp1: 'Temp.1',
      temp2: 'Temp.2',
      temperature: 'Teplota',
      total: 'Celkový',
      totalFlow: 'Celkový prietok',
      v: 'V',
      voltage: 'Napätie',
      weight1: 'Waga₁',
      weight2: 'Waga₂',
    },
    mustBeInteger: 'Musí byť celé číslo',
    outOfRange: 'Mimo rozsahu',
    range: 'Zakres',
    units: 'Jednostky',
  },
  validation: {
    required: 'Povinné',
    tooBigFile: 'Veľkosť súboru musí byť menšia ako 50 MB',
  },
  util: {
    schaefflerChart: {
      CrEquivalent: 'Ekvivalent Cr',
      NiEquivalent: 'Ekvivalent Ni',
    },
  },
  useImportJsonValidated: {
    unknownFileValidationError: 'Nahraný súbor sa nepodarilo overiť. Dáta nezodpovedajú očakávaniam',
  },
  tenantSettings: {
    dataSources: {
      headline: 'Zdroje dát',
      subheadline: 'Na tejto stránke sa spravujú zdroje dát Qmulus.',
    },
    license: {
      headline: 'Licenčný kľúč',
      subheadline: 'Táto stránka umožňuje prezerať a aktualizovať licenčný kľúč Qmulus.',
    },
    auditTrail: {
      headline: 'Záznam zmien',
      subheadline: 'Záznam zmien zaznamenáva každú úpravu vykonanú v aplikácii a priradí ju zodpovednému používateľovi, čím zabezpečuje úplnú sledovateľnosť všetkých akcií.',
    },
  },
  unitSystem,
  ...getComponentScopedTranslationMap('page', 'sk'),
  ...getComponentScopedTranslationMap('components', 'sk'),
  ...getComponentScopedTranslationMap('pt9800', 'sk'),
};
